import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../utils/apiClient';
import { Talk } from '../types';

const TalksPage: React.FC = () => {
  const [talks, setTalks] = useState<Talk[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchTalks();
  }, []);

  const fetchTalks = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get('/v1/talks');
      setTalks(response.data.data?.talks || []);
    } catch (err: any) {
      console.error('Error fetching talks:', err);

      // Only set error for actual failures, not empty results
      if (err.response) {
        // Server responded with error status (4xx, 5xx)
        setError(err.response.data?.error?.message || 'Failed to load talks');
      } else if (err.request) {
        // Network error - no response received
        setError('Network error. Please check your connection.');
      } else {
        // Other errors
        setError('An unexpected error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (talkId: string) => {
    if (!window.confirm('Are you sure you want to delete this talk?')) {
      return;
    }

    try {
      await apiClient.delete(`/v1/talks/${talkId}`);
      setTalks(talks.filter(talk => talk.talk_id !== talkId));
    } catch (err: any) {
      console.error('Error deleting talk:', err);
      alert(err.response?.data?.error?.message || 'Failed to delete talk');
    }
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          <i className="fas fa-microphone me-2"></i>
          My Talks
        </h2>
        <Link to="/talks/new" className="btn btn-primary">
          <i className="fas fa-plus me-2"></i>
          Create Talk
        </Link>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error}
        </div>
      )}

      {talks.length === 0 ? (
        <div className="card">
          <div className="card-body text-center py-5">
            <i className="fas fa-microphone fa-3x text-muted mb-3"></i>
            <h4>No Talks Yet</h4>
            <p className="text-muted">
              Create your first talk to get started with session proposals.
            </p>
            <Link to="/talks/new" className="btn btn-primary">
              <i className="fas fa-plus me-2"></i>
              Create Your First Talk
            </Link>
          </div>
        </div>
      ) : (
        <div className="row">
          {talks.map(talk => (
            <div key={talk.talk_id} className="col-md-6 mb-4">
              <div className="card h-100">
                <div className="card-body">
                  <h5 className="card-title">{talk.title}</h5>
                  <p className="card-text text-muted">{talk.summary}</p>
                  <div className="text-muted small mb-3">
                    <i className="fas fa-calendar me-1"></i>
                    Created {new Date(talk.created_at).toLocaleDateString()}
                  </div>
                </div>
                <div className="card-footer bg-white border-top-0">
                  <div className="d-flex gap-2">
                    <Link
                      to={`/talks/${talk.talk_id}`}
                      className="btn btn-sm btn-outline-primary"
                    >
                      <i className="fas fa-eye me-1"></i>
                      View
                    </Link>
                    <Link
                      to={`/talks/${talk.talk_id}/edit`}
                      className="btn btn-sm btn-outline-secondary"
                    >
                      <i className="fas fa-edit me-1"></i>
                      Edit
                    </Link>
                    <button
                      onClick={() => handleDelete(talk.talk_id)}
                      className="btn btn-sm btn-outline-danger"
                    >
                      <i className="fas fa-trash me-1"></i>
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TalksPage;
